<template>
    <v-card :loading="loading" flat class="ti-card mb-4">
        <div class="ti-head">
            <div class="ti-title-wrap">
                <span class="ti-eyebrow">{{ $t('TaskTitle') }}</span>
                <h2 class="ti-title">{{ taskInfo.title }}</h2>
            </div>
            <div class="ti-actions">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <button type="button" class="ti-btn" v-on="on" @click.stop="openTimelineDialog" :aria-label="$t('TaskTimeline')">
                            <v-icon small>mdi-timeline-clock-outline</v-icon>
                        </button>
                    </template>
                    <span>{{ $t('TaskTimeline') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <button type="button" class="ti-btn" v-on="on" @click.stop="openCommentsDialog" :aria-label="$t('ViewComments')">
                            <v-icon small>mdi-comment-text-multiple-outline</v-icon>
                        </button>
                    </template>
                    <span>{{ $t('ViewComments') }}</span>
                </v-tooltip>
            </div>
        </div>

        <div class="ti-facts">
            <div class="ti-fact">
                <span class="ti-fact-ic"><v-icon small>mdi-account-outline</v-icon></span>
                <div class="ti-fact-body">
                    <span class="ti-fact-lbl">{{ $t('AssignedTo') }}</span>
                    <span class="ti-fact-val">{{ taskInfo.owner || '—' }}</span>
                </div>
            </div>
            <div class="ti-fact">
                <span class="ti-fact-ic"><v-icon small>mdi-calendar-plus-outline</v-icon></span>
                <div class="ti-fact-body">
                    <span class="ti-fact-lbl">{{ $t('CreatedDate') }}</span>
                    <span class="ti-fact-val">{{ taskInfo.createdDate | formatDateTimeGregorian }}</span>
                </div>
            </div>
            <div class="ti-fact">
                <span class="ti-fact-ic"><v-icon small>mdi-calendar-start</v-icon></span>
                <div class="ti-fact-body">
                    <span class="ti-fact-lbl">{{ $t('StartDate') }}</span>
                    <span class="ti-fact-val">{{ taskInfo.plannedStartDate | formatDateTimeGregorian }}</span>
                </div>
            </div>
            <div class="ti-fact ti-fact--due">
                <span class="ti-fact-ic"><v-icon small>mdi-calendar-alert-outline</v-icon></span>
                <div class="ti-fact-body">
                    <span class="ti-fact-lbl">{{ $t('DueDate') }}</span>
                    <span class="ti-fact-val">{{ taskInfo.dueDate | formatDateTimeGregorian }}</span>
                </div>
            </div>
        </div>

        <v-dialog v-model="timelineDialog" transition="slide-x-transition" content-class="my-custom-dialog">
            <task-timeline v-if="timelineDialog" @close="timelineDialog = false" :taskId="taskId" />
        </v-dialog>
        <CommentsDialog v-model="commentsDialog" :comments="comments" :loading="commentsLoading" />
    </v-card>
</template>
<script>
    import TasksService from '@/services/TasksService.js'

    import NotesService from '@/services/NotesService.js'

    import TaskTimeline from '@/components/app/task/TaskTimeline.vue'
    import CommentsDialog from '@/components/controls/CommentsDialog.vue'

    export default {

        components: {
            TaskTimeline, CommentsDialog
        },

        props: {

            taskId: {
                type: Number,
                default: null
            }

        },

        data() {
            return {
                timelineDialog: false,
                commentsDialog: false,
                commentsLoading: false,
                comments: [],
                loading: false,
                taskInfo: {
                    title: ''
                }
            }
        },

        created() {
            this.loadTaskInfo()
        },

        methods: {

            openTimelineDialog() {
                this.timelineDialog = true
            },

            // all the comments of the process this task belongs to, not only this step's
            openCommentsDialog() {
                this.comments = []
                this.commentsLoading = true
                this.commentsDialog = true

                NotesService.listProcessComments(this.taskId).then(res => {
                    this.comments = res.data || []
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.commentsLoading = false
                })
            },

            loadTaskInfo() {
                this.loading = true

                TasksService.getTaskInfo(this.taskId).then(res => {
                    this.taskInfo = res.data
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            }
        }
    }
</script>
<style>
    .my-custom-dialog {
        width: 33% !important;
        min-height: 100% !important;
        position: absolute;
        left: 0;
        margin: 0;
    }
</style>
<style scoped>
    .ti-card {
        --ti-surface: #f3f6f5;
        --ti-line: #e6ecea;
        --ti-text: #1f2a28;
        --ti-muted: #6b7a77;
        --ti-primary: #0a716e;
        --ti-primary-tint: #e6f2f1;
        --ti-danger: #dc2626;
        border: 1px solid #e6ecea !important;
        border-radius: 18px !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18) !important;
        overflow: hidden;
    }

    .ti-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 14px;
        padding: 18px 22px 14px;
    }

    .ti-eyebrow {
        display: block;
        font-size: 11.5px;
        font-weight: 600;
        color: var(--ti-muted);
        margin-bottom: 2px;
    }

    .ti-title {
        margin: 0;
        font-size: 20px;
        font-weight: 800;
        line-height: 1.3;
        color: var(--ti-text);
    }

    .ti-actions {
        display: flex;
        gap: 8px;
        flex: 0 0 auto;
    }

    .ti-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border: 1px solid var(--ti-line);
        border-radius: 12px;
        background: #fff;
        cursor: pointer;
        transition: background-color .2s, border-color .2s, box-shadow .2s;
    }
    .ti-btn .v-icon { color: var(--ti-muted) !important; transition: color .2s; }
    .ti-btn:hover { background: var(--ti-primary-tint); border-color: transparent; }
    .ti-btn:hover .v-icon { color: var(--ti-primary) !important; }
    .ti-btn:focus { outline: none; }
    .ti-btn:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ti-primary); }

    .ti-facts {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
        padding: 0 12px 12px;
    }

    .ti-fact {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 12px;
        background: var(--ti-surface);
        min-width: 0;
    }

    .ti-fact-ic {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 32px;
        width: 32px;
        height: 32px;
        border-radius: 10px;
        background: #fff;
    }
    .ti-fact-ic .v-icon { color: var(--ti-primary) !important; }
    .ti-fact--due .ti-fact-ic .v-icon { color: #b45309 !important; }

    .ti-fact-body { display: flex; flex-direction: column; min-width: 0; }
    .ti-fact-lbl { font-size: 11px; font-weight: 600; color: var(--ti-muted); }
    .ti-fact-val {
        font-size: 13px;
        font-weight: 700;
        color: var(--ti-text);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    @media (max-width: 960px) {
        .ti-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
    @media (max-width: 600px) {
        .ti-facts { grid-template-columns: 1fr; }
        .ti-title { font-size: 17px; }
    }
</style>
